import { has } from 'lodash';

import {
  BUTTON_FLEX_ORDER,
  ENTITY_TYPES,
  FieldList,
  IBaseFormService,
  IFormGroupRules,
  IFormRules,
  TOOLBAR_BUTTON_POSITION,
  USER_CONFIG_MODULE_NAME,
  WIDGET_COMPONENT,
  XuxGridAction,
} from '../../types';
import { XUX_GRID_ACTIONS } from '@shared/types';
import { InventoryCountEditContainerComponent } from '@app/containers/inventory-counts/containers/edit/edit.inventory-count.container';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { BTN_DONE } from '@app/text-content/button-templates';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { WIDGET_FAMILY } from '@config/global-constants';
import {
  INVENTORY_COUNT_UNIT_OPTIONS,
  INVENTORY_COUNT_UNIT_VALUE,
} from '@app/shared/types/enums/inventory-count.enum';
import { I18n } from '@app/i18n/strings-keys';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';

export const createFormRules = (
  formService: IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT>,
  editor: InventoryCountEditContainerComponent,
): IFormRules => {
  const sections: FieldList = {
    inventory_count_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: ['inventory_count_section'],
    },
    {
      id: 'inventory_count_section',
      fields: [
        'name',
        'status',
        'start_date',
        'date',
        'virtual.set_count_unit',
        'inventory_count_items',
        'inventory_count_group_entity_id',
        'site_id',
        'counted_value',
        'variance_total',
      ],
    },
  ];

  return {
    bannerText: I18n.InfoMessages.PerformingACountWhileAnEventIsOpenMessage,
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        inventory_count_items: {
          type: 'array',
        },
        set_count_unit: {
          type: 'string',
        },
      },
    },
    fields: {
      ...sections,
      name: {
        show: false,
      },
      date: {
        show: false,
      },
      start_date: {
        show: false,
      },
      inventory_count_group_entity_id: {
        show: false,
      },
      status: {
        show: false,
      },
      site_id: {
        show: false,
      },
      counted_value: {
        defaultValue: 0,
        show: false,
      },
      variance_total: {
        defaultValue: 0,
        show: false,
      },
      'virtual.set_count_unit': {
        title: I18n.Forms.InventoryCountForm.SetCountUnit,
        widget: WIDGET_COMPONENT.select,
        widgetFamily: WIDGET_FAMILY.XUX,
        defaultValue: INVENTORY_COUNT_UNIT_VALUE.BaseUnit,
        selectOptions: INVENTORY_COUNT_UNIT_OPTIONS,
        widgetData: {
          hideEmptyOption: true,
        },
      },
      inventory_count_items: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.EntityNames.InventoryCount,
        class: `navigate-grid one-column-tree-grid inventory-count-items-grid fixed-scrollbar-grid
          ${formService.isCategoryListView() ? '' : 'inventory-count-list-view'}
          `,
        minItems: 0,
        widgetData: {
          artificialInputCheckForSearchPanel: true,
          showLoader: formService.isInventoryItemVenuesGridBusy$,
          showPristineErrors: false,
          filterable: true,
          showSearch: true,
          multiselect: false,
          disableColumnSelector: formService.isCategoryListView(),
          orderControls: false,
          treeViewMode: formService.isCategoryListView(),
          defaultExpanded: formService.isCategoryListView(),
          ignoreItemValueOptimization: true,
          treeChildrenPropertyName: 'children',
          isLeafRow: (data) => {
            return has(data, 'isLeaf');
          },
          valueField: INTERNAL_GRID_ROW_ID,
          alwaysFindNextEditCell: true,
          focusCellInnerElWithDebounce: true,
          inlineEditOptions: {
            beforeActionMappers: [formService.getInlineChangeMapper()],
            beforeActionItemsMapper: formService.isCategoryListView()
              ? [formService.getInlineChangeMapperItemForTree()]
              : [formService.getInlineChangeMapperItemsForList()],
            updateOtherGridItemFunc:
              formService.handleUpdateOtherGridItem.bind(formService),
          },
          userGridOptions: {
            suppressAutoSize: !formService.isCategoryListView(),
            noGridHeight: true,
            deltaRowDataMode: false,
            suppressMovableColumns: formService.isCategoryListView(),
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            rowClassRules: formService.getListGridRowCellClass(),
            suppressHorizontalScroll: false,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onDragStopped: (params): void => {
              formService.saveUserConfig(
                USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
              );
            },
            onColumnMoved: (params): void => {
              formService.autoAdjustMovedColumn(
                params?.toIndex,
                params?.column?.colId,
                params?.source,
              );
            },
            overlayNoRowsTemplate: formService.i18nService.instant(
              I18n.Common.NoRowsToShow,
            ),
          },
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]: (
              action: XuxGridAction,
              grid: XuxEditGridComponent,
            ): void =>
              formService.handleListGridReady.call(formService, action, grid, {
                canEdit: (): boolean => editor.canEdit,
              }),
            [XUX_GRID_ACTIONS.SORT_MODEL_CHANGED]:
              formService.handleSortingChange.bind(formService),
            [XUX_GRID_ACTIONS.COLUMN_SELECTION_CHANGED]:
              formService.handleColumnSelectChange.bind(formService),
          },
          columnsObs: formService.columns$,
          buttons: [
            {
              flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
              caption: I18n.Forms.InventoryCountForm.ClearCount,
              hiddenObs: formService.isFinalStatus$,
              disabledObs: combineLatest([
                formService.isDisableGridButtons$,
                formService.isDisableClearButtons$,
              ]).pipe(
                map(
                  ([isDisableGridButtons, isDisableClearButtons]: [
                    boolean,
                    boolean,
                  ]): boolean => isDisableGridButtons || isDisableClearButtons,
                ),
              ),
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              class: 'btn btn-default',
              click: formService.clearAllCount.bind(formService),
            },
            {
              ...BTN_DONE,
              flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
              caption: I18n.Forms.InventoryCountForm.WriteZerosToBlankCounts,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              class: 'btn btn-default',
              click: formService.writeCountsToBlankCounts.bind(formService),
              disabledObs: formService.isDisableGridButtons$,
              hiddenObs: formService.isFinalStatus$,
            },
          ],
        },
      },
    },
    groups: [...panelGroups],
  };
};
